import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
  DataSource,
  DataSourcesService,
} from '../../../services/data-sources.service';
import { Tutor, TutorService } from '../../../services/tutor.service';
import { isSystemBucket } from '../../../services/bucket.service';

@Component({
  selector: 'app-knowledge-select-form',
  templateUrl: './knowledge-select-form.component.html',
  styleUrl: './knowledge-select-form.component.scss',
})
export class KnowledgeSelectFormComponent implements AfterViewInit {
  @Input() tutor?: Tutor;
  @Input() searchText = '';
  /** Which agent this assignment binds to. Defaults to 'tutor' so legacy
   *  call sites (no `[purpose]` set) keep their previous behavior. */
  @Input() purpose: 'tutor' | 'dozent' = 'tutor';
  /** The link was this agent's first knowledge and al-tutor switched
   *  „Einschränkungen der Wissensbasis" to „Nur Wissensbasis" (migration 224,
   *  `knowledge_mode_switched` on the PATCH response). The host page shows
   *  the hint and reloads the settings — the wizard ignores it. */
  @Output() knowledgeModeSwitched = new EventEmitter<void>();

  constructor(
    protected tutorService: TutorService,
    protected dataSourcesService: DataSourcesService,
    private router: Router
  ) {
    this.dataSourcesService.listDataSources().subscribe(() => {});
  }

  /** Bucket id behind a bucket-backed data source.
   *
   *  `create_bucket` (al-filemanager `crud/bucket_ops.py`) writes the id
   *  into `source_metadata.bucket_id` — NOT into `location`, which stays
   *  null for buckets. `location` is only checked as a fallback for
   *  legacy rows. Returns null for non-bucket sources (website, filter,
   *  …), which have no knowledge-base page to open. */
  bucketIdOf(s: DataSource): string | null {
    return (
      s?.source_metadata?.bucket_id ||
      (s?.type === 'bucket' ? s.location : null) ||
      null
    );
  }

  /** Deep-link into the knowledge base page. `stopPropagation` keeps the
   *  surrounding tile click (assignment toggle) from firing. */
  openBucket(s: DataSource, event: MouseEvent): void {
    event.stopPropagation();
    const bucketId = this.bucketIdOf(s);
    if (!bucketId) return;
    this.router.navigate(['/knowledge-base'], { queryParams: { bucketId } });
  }

  ngAfterViewInit() {}

  /** Read the right list off the tutor for the current purpose. Falls
   *  back to the legacy `data_source_ids` (union) when the new
   *  purpose-split fields aren't populated yet. */
  private currentIds(): string[] {
    if (!this.tutor) return [];
    if (this.purpose === 'dozent') {
      return this.tutor.dozent_data_source_ids ?? [];
    }
    return (
      this.tutor.tutor_data_source_ids ??
      this.tutor.data_source_ids ??
      []
    );
  }

  onSelect(dataSourceId: string) {
    if (!this.tutor) {
      console.error('Tutor is not defined');
      return;
    }

    const current = this.currentIds();
    const next = current.includes(dataSourceId)
      ? current.filter((id) => id !== dataSourceId)
      : [...current, dataSourceId];

    // Write back to the tutor object first so the UI reflects the new
    // selection immediately (the isSelected check reads from `tutor`).
    if (this.purpose === 'dozent') {
      this.tutor.dozent_data_source_ids = next;
    } else {
      this.tutor.tutor_data_source_ids = next;
      this.tutor.data_source_ids = next; // keep legacy field in sync
    }

    // Send only the purpose that changed — backend leaves other purposes
    // untouched (see purpose-aware update_tutor_metadata).
    const patch: any = { ...this.tutor };
    if (this.purpose === 'dozent') {
      patch.dozent_data_source_ids = next;
      delete patch.tutor_data_source_ids;
      delete patch.data_source_ids;
    } else {
      patch.tutor_data_source_ids = next;
      delete patch.dozent_data_source_ids;
    }

    this.tutorService.updateTutor(patch as Tutor).subscribe((res: any) => {
      if (this.purpose === 'tutor' && res?.data?.knowledge_mode_switched) {
        this.knowledgeModeSwitched.emit();
      }
    });
  }

  isSelected(dataSourceId: string) {
    return this.currentIds().includes(dataSourceId);
  }

  /** A lecturer's internal folder (`__dozent_knowledge__`). It stays in this
   *  picker — its files reach an agent only through this link — but shows as
   *  „Dozenten-Dateien · <Name>" (`knowledgeSourceName`), #230. */
  isSystem(s: DataSource): boolean {
    return isSystemBucket(s);
  }

  get sources() {
    const all = this.dataSourcesService.dataSources$.value;
    if (!this.searchText) return all;
    const q = this.searchText.toLowerCase();
    return all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (this.isSystem(s) && (s.owner_name ?? '').toLowerCase().includes(q))
    );
  }
}
